import { createSharedComposable, useColorMode } from '@vueuse/core'
import { computed } from 'vue'

export type ThemePreference = 'auto' | 'light' | 'dark'

/**
 * Cycle order. A binary toggle cannot express "follow the OS": picking `light`
 * on a light-mode OS would silently pin the theme with no affordance to get
 * back to `auto`. Three states, one button.
 */
const CYCLE: readonly ThemePreference[] = ['auto', 'light', 'dark']

/**
 * localStorage is user-writable and may hold a key from an older build, so the
 * stored value is narrowed here rather than at each consumer — otherwise every
 * reader (icon choice, aria-label lookup, cycle position) needs its own notion
 * of what a valid preference is, and they drift.
 */
function isThemePreference(value: unknown): value is ThemePreference {
  return CYCLE.includes(value as ThemePreference)
}

/**
 * Next preference in the cycle. Pure and exported so the ordering is testable
 * without standing up the shared composable's DOM singleton.
 */
export function nextTheme(current: ThemePreference): ThemePreference {
  const index = CYCLE.indexOf(current)
  // An unrecognised stored value (hand-edited localStorage, or a key written by
  // an older build) resolves to the start of the cycle rather than NaN.
  return index === -1 ? CYCLE[0] : CYCLE[(index + 1) % CYCLE.length]
}

/**
 * Shared theme state.
 *
 * `useColorMode` writes the resolved mode as a class onto `<html>`, which is
 * what `@custom-variant dark (&:is(.dark *))` in styles.css keys on, and
 * persists the preference to localStorage under `vueuse-color-scheme` — the
 * same key the anti-flash script in index.html reads.
 *
 * Shared rather than per-caller because App.vue drives the toggle while
 * MapView watches the resolved mode to restyle its Leaflet vectors; two
 * independent instances would each attach their own storage listener.
 */
export const useTheme = createSharedComposable(() => {
  const mode = useColorMode({ disableTransition: true })

  /** What the user chose — may be `auto`. Never anything outside the cycle. */
  const preference = computed<ThemePreference>(() =>
    isThemePreference(mode.store.value) ? mode.store.value : 'auto',
  )

  /**
   * What `auto` actually resolves to right now. Drives the map restyle.
   *
   * Anything that is not `dark` reports as `light`, which is what the
   * stylesheet does too: only `.dark` overrides the `:root` tokens, so an
   * unrecognised stored value renders light. Keeping the declared type honest
   * matters because MapView reads tokens on the strength of it.
   */
  const resolved = computed<'light' | 'dark'>(() =>
    mode.state.value === 'dark' ? 'dark' : 'light',
  )

  function cycleTheme() {
    mode.value = nextTheme(preference.value)
  }

  return { preference, resolved, cycleTheme }
})
